<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ClipboardDocumentCheckIcon,
  LinkIcon,
  LinkIcon as Link2Icon,
  LinkSlashIcon as Link2OffIcon,
  CheckCircleIcon,
  BoltSlashIcon,
} from '@heroicons/vue/24/solid';
import { getEffectiveTrackMatchStatusId } from '@/constants/trackMatchStatus';
import { markButtonShowsCheckIcon } from '@/trackMatching/trackMatchMarkAction';
import {
  useTrackMatchingQueueActions,
  type TrackMatchingActionRow,
} from '@/trackMatching/useTrackMatchingQueueActions';
import {
  RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
  SOURCE_TYPES,
  useTrackMatchingStore,
} from '@/stores/trackMatchingStore';

const props = defineProps<{
  row: TrackMatchingActionRow & {
    rawDdexExpandKind?: 'more' | 'less';
    rawDdexHiddenCount?: number;
  };
  isLoading: boolean;
  /** When true, row actions are disabled (live track match status is Processing). */
  isLiveTrackProcessing?: boolean;
  /** Raw DDEX list “show more” / “show less” control rows (Actions column only). */
  expandRawDdexMatchesForLiveTrack?: (liveTrackId: string | number) => void;
  collapseRawDdexMatchesForLiveTrack?: (liveTrackId: string | number) => void;
}>();

const trackMatchingStore = useTrackMatchingStore();
const { isRowActionSelected, queueRowAction, getMarkDoneTooltip, isRevealDoneMatches } =
  useTrackMatchingQueueActions(trackMatchingStore);

function markDoneShowsFilledCheck(row: TrackMatchingActionRow): boolean {
  return markButtonShowsCheckIcon({
    persistedStatusId: getEffectiveTrackMatchStatusId(row as Record<string, unknown>),
    markQueued: isRowActionSelected(row, 'markdone'),
    revealDoneMatches: isRevealDoneMatches(row),
  });
}

function markDoneAriaLabel(row: TrackMatchingActionRow): string {
  if (markDoneShowsFilledCheck(row)) {
    if (isRowActionSelected(row, 'markdone')) {
      return 'Mark Later queued (save to apply)';
    }
    return 'Marked Later — candidates hidden';
  }
  return 'Mark Later or clear Later';
}
</script>

<template>
  <!--
    The whole Actions cell is marked so a click in it never opens the row.
    A click on a disabled button, or on the space around the buttons, would otherwise reach the row.
  -->
  <div class="flex flex-wrap items-center gap-1" data-row-click-ignore>
    <template v-if="isLoading">
      <Skeleton class="h-4 w-3/4" />
    </template>
    <template
      v-else-if="
        row.source === RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE &&
        props.expandRawDdexMatchesForLiveTrack &&
        props.collapseRawDdexMatchesForLiveTrack
      "
    >
      <Tooltip v-if="row.rawDdexExpandKind === 'more'">
        <TooltipTrigger as-child>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="raw-ddex-matches-show-more"
            class="h-8 gap-1.5 whitespace-nowrap bg-background px-2.5 text-xs font-medium text-foreground shadow-none hover:bg-accent hover:text-accent-foreground dark:bg-card dark:hover:bg-accent dark:hover:text-accent-foreground"
            :aria-label="`Show more Raw DDEX matches (${row.rawDdexHiddenCount ?? 0} hidden)`"
            @click="props.expandRawDdexMatchesForLiveTrack!(row.live_track_id)"
          >
            <ArrowDownIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Show more
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start">
          <p>Show {{ row.rawDdexHiddenCount ?? 0 }} more Raw DDEX matches</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip v-else>
        <TooltipTrigger as-child>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="raw-ddex-matches-show-less"
            class="h-8 gap-1.5 whitespace-nowrap bg-background px-2.5 text-xs font-medium text-foreground shadow-none hover:bg-accent hover:text-accent-foreground dark:bg-card dark:hover:bg-accent dark:hover:text-accent-foreground"
            aria-label="Show fewer Raw DDEX matches"
            @click="props.collapseRawDdexMatchesForLiveTrack!(row.live_track_id)"
          >
            <ArrowUpIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Show less
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start">
          <p>Show fewer Raw DDEX matches</p>
        </TooltipContent>
      </Tooltip>
    </template>
    <template v-else-if="row.source === SOURCE_TYPES.SPOTIFY">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            :disabled="isLiveTrackProcessing"
            :variant="isRowActionSelected(row, 'isrc') ? 'accent' : 'ghost'"
            aria-label="Apply ISRC"
            class="group hover:text-accent-foreground"
            @click="row.isrc && queueRowAction('isrc', row)"
          >
            <ClipboardDocumentCheckIcon
              class="w-4 h-4 group-hover:text-accent-foreground"
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Apply ISRC</p>
        </TooltipContent>
      </Tooltip>
    </template>
    <template v-else-if="row.source === SOURCE_TYPES.LIVE_TRACK">
      <template v-if="row.raw_ddex_track_id">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              :disabled="isLiveTrackProcessing"
              :variant="isRowActionSelected(row, 'unlink') ? 'accent' : 'ghost'"
              aria-label="Unlink Track"
              class="group hover:text-accent-foreground"
              @click="queueRowAction('unlink', row)"
            >
              <Link2OffIcon
                v-if="isRowActionSelected(row, 'unlink')"
                class="w-4 h-4 group-hover:text-accent-foreground"
              />
              <Link2Icon v-else class="w-4 h-4 group-hover:text-accent-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{{ isRowActionSelected(row, 'unlink') ? 'Link Broken' : 'Unlink Track' }}</p>
          </TooltipContent>
        </Tooltip>
      </template>
      <!-- Mark Later: only when not linked, or linked and Unlink selected (save can unlink + set Later) -->
      <template v-if="!row.raw_ddex_track_id || isRowActionSelected(row, 'unlink')">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              :disabled="isLiveTrackProcessing"
              :variant="isRowActionSelected(row, 'markdone') ? 'accent' : 'ghost'"
              :aria-label="markDoneAriaLabel(row)"
              class="group hover:text-accent-foreground"
              @click="queueRowAction('markdone', row)"
            >
              <CheckCircleIcon
                v-if="markDoneShowsFilledCheck(row)"
                class="w-4 h-4 group-hover:text-accent-foreground"
              />
              <BoltSlashIcon v-else class="w-4 h-4 group-hover:text-accent-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{{ getMarkDoneTooltip(row.live_track_id) }}</p>
          </TooltipContent>
        </Tooltip>
      </template>
    </template>
    <template v-else-if="row.source === SOURCE_TYPES.RAW_DDEX">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            :disabled="isLiveTrackProcessing"
            :variant="isRowActionSelected(row, 'link') ? 'accent' : 'ghost'"
            aria-label="Link Track"
            class="group hover:text-accent-foreground"
            @click="queueRowAction('link', row)"
          >
            <LinkIcon class="w-4 h-4 group-hover:text-accent-foreground" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{{ isRowActionSelected(row, 'link') ? 'Currently Linked' : 'Link Track' }}</p>
        </TooltipContent>
      </Tooltip>
    </template>
  </div>
</template>
